/**
 * Admin Revenue Analytics – real data from dashboard metrics
 */
import React, { useEffect, useState, useRef } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Spinner from '../common/Spinner';
import Select from '../common/Select';

const AdminRevenueAnalytics = () => {
  const { loadDashboardMetrics, dashboardMetrics, loading } = useAdmin();
  const { showError } = useToast();
  const { formatCurrency, formatNumber, language } = useI18n();
  const [timeRange, setTimeRange] = useState('30d');
  const [statsInView, setStatsInView] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        await loadDashboardMetrics({ timeRange });
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement' : 'Failed to load')
        );
      }
    };
    loadData();
  }, [timeRange, loadDashboardMetrics, showError, language]);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => e.isIntersecting && setStatsInView(true));
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (loading && !dashboardMetrics) {
    return (
      <div className="transactions-wt-loading">
        <div className="dashboard-wt-hero-inner">
          <Spinner size="lg" />
          <p className="mt-4 text-[#a3a3a3]">
            {language === 'fr' ? 'Chargement...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  const metrics = dashboardMetrics?.metrics || dashboardMetrics || {};
  const financial = metrics.financial || {};
  const totalRevenue = Number(financial.totalRevenue) || 0;
  const platformFees = Number(financial.platformFees) || 0;
  const withdrawalFees = Number(financial.withdrawalFees) || 0;
  const transactionCount = Number(financial.transactionCount) || 0;

  const statCards = [
    { icon: '💰', label: language === 'fr' ? 'Revenus totaux' : 'Total revenue', value: formatCurrency(totalRevenue, 'XAF') },
    { icon: '💳', label: language === 'fr' ? 'Frais plateforme' : 'Platform fees', value: formatCurrency(platformFees, 'XAF') },
    { icon: '📤', label: language === 'fr' ? 'Frais de retrait' : 'Withdrawal fees', value: formatCurrency(withdrawalFees, 'XAF') },
    { icon: '📦', label: language === 'fr' ? 'Transactions complétées' : 'Completed transactions', value: formatNumber(transactionCount) },
  ];

  return (
    <>
      <div className="dashboard-wt-card mb-6">
        <div className="dashboard-wt-card-header">
          <h2 className="dashboard-wt-card-title">
            💰 {language === 'fr' ? 'Analytique Revenus' : 'Revenue Analytics'}
          </h2>
        </div>
        <div className="dashboard-wt-card-content">
          <div className="transactions-wt-form-row">
            <div className="transactions-wt-form-select" style={{ maxWidth: 200 }}>
              <Select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                options={[
                  { value: '24h', label: language === 'fr' ? '24 heures' : '24 Hours' },
                  { value: '7d', label: language === 'fr' ? '7 jours' : '7 Days' },
                  { value: '30d', label: language === 'fr' ? '30 jours' : '30 Days' },
                  { value: '90d', label: language === 'fr' ? '90 jours' : '90 Days' },
                  { value: '1y', label: language === 'fr' ? '1 an' : '1 Year' },
                ]}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`dashboard-wt-stats ${statsInView ? 'is-inview' : ''}`} ref={statsRef}>
        {statCards.map((stat, i) => (
          <div
            key={i}
            className="dashboard-wt-stat-card"
            style={{ animationDelay: `${i * 0.1}s` }}
          >
            <div className="dashboard-wt-stat-icon">
              <span>{stat.icon}</span>
            </div>
            <div className="dashboard-wt-stat-content">
              <div className="dashboard-wt-stat-value">{stat.value}</div>
              <div className="dashboard-wt-stat-label">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AdminRevenueAnalytics;
